import React, { useEffect, useState } from "react";
import { Mic2, Sparkles, Zap, CheckCircle2, AlertCircle, Radio } from "lucide-react";
import { api } from "@/lib/api";

const PACKAGES = [
  { id: "show_1min",   title: "1-min show",                       price: 12, badge: "Quick drop",         length: "1 minute",  bundle: false },
  { id: "show_3min",   title: "3-min show",                       price: 25, badge: "Just the show",     length: "3 minutes", bundle: false },
  { id: "bundle_1min", title: "1-min show + 7-day ad slot",       price: 45, badge: "Bundle · save $2",  length: "1 minute",  bundle: true,  savedVs: 47 },
  { id: "bundle_3min", title: "3-min show + 7-day ad slot",       price: 60, badge: "Best deal · save $10", length: "3 minutes", bundle: true,  savedVs: 70 },
];

export default function Produce() {
  const [templates, setTemplates] = useState([]);
  const [pickedTemplate, setPickedTemplate] = useState(null);
  const [pkg, setPkg] = useState("show_3min");
  const [biz, setBiz] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/produce/templates");
        if (alive) setTemplates(data || []);
      } catch (_) {}
    })();
    return () => { alive = false; };
  }, []);

  const start = async () => {
    setErr("");
    if (!biz.trim()) { setErr("Tell us your business or show name."); return; }
    if (!email.trim() || !email.includes("@")) { setErr("Enter a valid contact email."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/produce/checkout", {
        package_id: pkg,
        business_name: biz.trim(),
        contact_email: email.trim(),
        origin_url: window.location.origin,
      });
      // Persist the picked template so the wizard can pre-fill answers
      if (pickedTemplate) {
        sessionStorage.setItem(`fliboe_template_${data.wizard_token}`, JSON.stringify(pickedTemplate));
      }
      window.location.href = data.url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not start checkout. Try again.");
      setBusy(false);
    }
  };

  return (
    <main className="max-w-5xl mx-auto px-5 py-16" data-testid="produce-page">
      <div className="text-center">
        <div className="fl-mono text-[11px] tracking-[0.3em] uppercase text-emerald-500 flex items-center justify-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> AI-produced shows
        </div>
        <h1 className="fl-display text-5xl sm:text-6xl font-extrabold tracking-tighter mt-4">
          We'll write it.<br className="hidden sm:inline" /> <span className="text-emerald-500">We'll voice it.</span>
        </h1>
        <p className="text-slate-400 max-w-2xl mx-auto mt-5">
          Pick a template, answer 6 quick questions, and 30 seconds later you have a finished radio show or ad — script, voice, and all.
        </p>
      </div>

      {/* Templates */}
      <h2 className="fl-display text-2xl font-bold mt-12 mb-4">1 · Pick a starting template</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {templates.map((t) => (
          <button
            key={t.id}
            onClick={() => { setPickedTemplate(t); if (!biz && t.id === "artist") setBiz(""); }}
            className={`fl-card p-4 text-left transition ${pickedTemplate?.id === t.id ? "ring-2 ring-emerald-500 bg-emerald-500/5" : "hover:bg-white/5"}`}
            data-testid={`produce-template-${t.id}`}
          >
            <div className="text-2xl">{t.emoji}</div>
            <div className="text-white font-semibold mt-2">{t.title}</div>
            <div className="text-xs text-slate-400 mt-1 leading-relaxed">
              {t.defaults.tagline}
            </div>
          </button>
        ))}
      </div>

      {/* Packages */}
      <h2 className="fl-display text-2xl font-bold mt-12 mb-4">2 · Pick a package</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        {PACKAGES.map((p) => (
          <button
            key={p.id}
            onClick={() => setPkg(p.id)}
            className={`fl-card p-5 text-left transition ${pkg === p.id ? "ring-2 ring-emerald-500 bg-emerald-500/5" : "hover:bg-white/5"}`}
            data-testid={`produce-pkg-${p.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="fl-mono text-[10px] tracking-[0.25em] uppercase text-emerald-500">{p.badge}</div>
                <div className="fl-display text-xl font-bold mt-1.5">{p.title}</div>
                <div className="text-xs text-slate-400 mt-1">{p.length} · AI script + voice · 2 free re-tries</div>
              </div>
              <div className="text-right shrink-0">
                <div className="fl-display text-3xl font-extrabold tracking-tighter">${p.price}</div>
                {p.bundle && <div className="text-[10px] text-emerald-400 mt-0.5">{`Save $${p.savedVs - p.price}`}</div>}
              </div>
            </div>
            <div className="mt-3 text-xs text-slate-300 space-y-1">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Hooked-up Claude script</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Studio-grade AI voice (your vibe)</div>
              {p.bundle && (
                <>
                  <div className="flex items-center gap-2"><Radio className="w-4 h-4 text-emerald-500" /> Airs every few songs for 7 days</div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Auto-submitted for admin review</div>
                </>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Buyer */}
      <div className="fl-card p-6 mt-8">
        <h3 className="fl-display text-xl font-bold mb-4">3 · Who's it for?</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs fl-mono uppercase tracking-widest text-slate-400">Business / show name</label>
            <input value={biz} onChange={(e) => setBiz(e.target.value)} placeholder="Joe's Pizza · 7th Avenue" className="fl-input mt-1.5" data-testid="produce-business" />
          </div>
          <div>
            <label className="text-xs fl-mono uppercase tracking-widest text-slate-400">Email (for receipt + the audio file)</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@business.com" className="fl-input mt-1.5" data-testid="produce-email" />
          </div>
        </div>
        {err && (
          <div className="mt-3 flex items-start gap-2 text-sm text-red-400" data-testid="produce-error">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {err}
          </div>
        )}
        <button onClick={start} disabled={busy} className="fl-btn mt-5 w-full sm:w-auto" data-testid="produce-checkout">
          <Zap className="w-4 h-4" />
          {busy ? "Opening Stripe…" : `Pay $${PACKAGES.find(p => p.id === pkg)?.price} · Make my show →`}
        </button>
        <p className="text-xs text-slate-500 mt-3">
          After payment, a quick 6-question wizard pops up. Your show is ready in about 30 seconds. Don't love it? 2 free re-tries included.
        </p>
      </div>
    </main>
  );
}
